// src/components/Navbar.js
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const NavLink = ({ to, children, active }) => (
  <Link
    to={to}
    className={`px-3 py-2 rounded-md text-sm font-medium transition
      hover:bg-white/10 hover:text-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60
      ${active ? "bg-white/15 text-white" : "text-white/90"}`}
  >
    {children}
  </Link>
);

const Navbar = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout?.();
    localStorage.removeItem("authToken");
    navigate("/");
  }, [logout, navigate]);

  // Close mobile menu on route change (or on Escape)
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50">
      <nav className="bg-[#e81b2c] bg-gradient-to-r from-[#e81b2c] to-[#8e2790] text-white shadow-md backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-4">
          {/* Top bar */}
          <div className="flex h-14 items-center justify-between">
            {/* Brand */}
            <Link
              to="/dashboard"
              className="text-lg sm:text-2xl font-bold tracking-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded"
            >
              Beverage Distribution
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2">
              <NavLink to="/dashboard" active={isActive("/dashboard")}>
                Dashboard
              </NavLink>
              <NavLink to="/products" active={isActive("/products")}>
                Products
              </NavLink>
              <NavLink to="/sale/history" active={isActive("/sale")}>
                Sales
              </NavLink>
              <NavLink
                to="/inventory"
                className="hover:text-white/80"
                active={isActive("/inventory")}
              >
                Inventory
              </NavLink>
              <NavLink
                to="/retailers"
                className="hover:text-white/80"
                active={isActive("/retailers")}
              >
                Retailers
              </NavLink>
              <NavLink
                to="/sale/add-sale"
                className="hover:text-white/80 text-2xl"
              >
                Add Sale
              </NavLink>
              <NavLink
                to="/profile"
                className="hover:text-white/80"
                active={isActive("/profile")}
              >
                Profile
              </NavLink>
              <button
                onClick={handleLogout}
                className="ml-2 px-3 py-2 rounded-md text-sm font-medium bg-white/10 text-white hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                Logout
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-white/90 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-controls="primary-menu"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              <span className="sr-only">Toggle main menu</span>
              {open ? (
                // X icon
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                // Hamburger icon
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile panel */}
          <div
            id="primary-menu"
            className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300
              ${open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
          >
            <div className="pb-3 pt-2 border-t border-white/10">
              <div className="flex flex-col gap-1">
                <NavLink to="/dashboard" active={isActive("/dashboard")}>
                  Dashboard
                </NavLink>
                <NavLink to="/products" active={isActive("/products")}>
                  Products
                </NavLink>
                <NavLink to="/sales" active={isActive("/sales")}>
                  Sales
                </NavLink>
                <NavLink
                  active={isActive("/retailers")}
                  to="/retailers">
                  Retailers
                </NavLink>
                <NavLink to="/inventory/inward/new">Add Inward</NavLink>
                <button
                  onClick={handleLogout}
                  className="mt-1 w-full text-left px-3 py-2 rounded-md text-sm font-medium bg-white/10 text-white hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
